import { useForm } from '@tanstack/react-form';
import { Input, Modal, Text } from '@ui/themed';
import { t } from 'i18next';
import { ConfirmPasswordError } from '../errors';
import { useConfirmPassword } from '../hooks/use-confirm-password';
import { type ConfirmPasswordFormState, confirmPasswordFormSchema } from '../schemas';

export function ConfirmPasswordModal({
  isOpen,
  onClose,
  onConfirmed,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirmed: () => void;
}) {
  const confirmPassword = useConfirmPassword();

  const form = useForm({
    defaultValues: { password: '' } satisfies ConfirmPasswordFormState,
    validators: {
      onSubmit: confirmPasswordFormSchema,
    },
    onSubmit: ({ value }) => {
      const payload = confirmPasswordFormSchema.parse(value);

      confirmPassword.mutate(payload, {
        onSuccess: () => {
          form.reset();
          onConfirmed();
        },
        onError: (error) => {
          if (error instanceof ConfirmPasswordError && error.id === 'incorrect_password') {
            form.setFieldMeta('password', (meta) => ({
              ...meta,
              errorMap: {
                ...meta.errorMap,
                onSubmit: {
                  message: t(
                    'features:account.components.confirm_password_modal.errors.password_incorrect',
                  ),
                },
              },
            }));
          }
        },
      });
    },
  });

  function handleClose() {
    if (confirmPassword.isPending) {
      return;
    }
    form.reset();
    confirmPassword.reset();
    onClose();
  }

  function submit() {
    // Clear errors so we can re-validate
    form.setErrorMap({ onSubmit: { fields: {} } });

    form.handleSubmit();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      primaryAction={{
        text: t('features:account.components.confirm_password_modal.action'),
        onClick: submit,
        loading: confirmPassword.isPending,
        disabled: confirmPassword.isPending,
      }}
      secondaryAction={{
        text: t('buttons:cancel'),
        onClick: handleClose,
        disabled: confirmPassword.isPending,
      }}
    >
      <Modal.Header>{t('features:account.components.confirm_password_modal.title')}</Modal.Header>
      <Modal.Body>
        {/* The submit button lives in the modal footer, outside this form; the form element is
            here so that pressing Enter in the single password field submits too. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();

            submit();
          }}
        >
          <div className="flex flex-col gap-4">
            <Text variant="secondary">
              {t('features:account.components.confirm_password_modal.description')}
            </Text>
            <form.Field name="password">
              {(field) => (
                <Input
                  label={t('features:account.components.confirm_password_modal.password_label')}
                  type="password"
                  placeholder="••••••••••••••••"
                  value={field.state.value}
                  onChange={field.handleChange}
                  maxLength={128}
                  error={field.state.meta.errors[0]?.message}
                />
              )}
            </form.Field>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
